import * as THREE from 'three';
import {
    expandImportedModelRenderableTargets,
    ImportedAssemblyPartTargetResolver,
} from '../highlight/ImportedAssemblyPartTargetResolver';
import { ImportedBodyLeafTargetResolver } from '../highlight/ImportedBodyLeafTargetResolver';
import { ImportedModelOverlayRenderer } from '../highlight/ImportedModelOverlayRenderer';
import {
    ImportedModelSelectionObjectResolver,
} from '../selection/ImportedModelSelectionObjectResolver';
import type {
    SelectionExtension,
    SelectionExtensionCommandSelectionPayload,
    SelectionExtensionContext,
    SelectionExtensionFaceSelectionPayload,
    SelectionExtensionRenderContext,
    SelectionExtensionSelectionPayload,
    SelectionTargetObject,
} from './selection-extension';

/**
 * GLB imported-model 的 SelectionExtension 兼容桥接层。
 *
 * 设计说明：
 * 1. 保留旧 `SelectionExtension` 协议，避免外部调用方立刻改造；
 * 2. 普通实体高亮统一下沉到 Task 2 的 resolver + overlay renderer；
 * 3. bridge 自身不再管理 mesh 子高亮、线框缓存与 GPU 资源生命周期，只负责协议适配与路由。
 */
export class GlbSelectionExtension implements SelectionExtension {
    private readonly overlayRendererByScene = new Map<THREE.Scene, ImportedModelOverlayRenderer>();
    private readonly bodyLeafTargetResolver = new ImportedBodyLeafTargetResolver();
    private readonly assemblyPartTargetResolver = new ImportedAssemblyPartTargetResolver();

    /**
     * 收集可参与 imported-model 拾取的 mesh / line。
     *
     * @param context 选择上下文。
     * @returns 命中候选。
     */
    public collectRaycastTargets(context: SelectionExtensionContext): { meshes: THREE.Mesh[]; lines: THREE.Line[] } {
        const meshes: THREE.Mesh[] = [];
        const lines: THREE.Line[] = [];

        context.scene.traverse((object) => {
            if (!context.isObjectActuallyVisible(object) || context.isRaycastExcludedObject(object)) {
                return;
            }
            if (!this.isGlbHierarchyObject(object)) {
                return;
            }
            if (object instanceof THREE.Mesh) {
                meshes.push(object);
                return;
            }
            if (object instanceof THREE.Line && !(object instanceof THREE.LineSegments)) {
                lines.push(object);
            }
        });

        return { meshes, lines };
    }

    /**
     * 解析射线命中对象对应的 mesh id。
     *
     * @param hitObject 命中的对象。
     * @param context 选择上下文。
     * @returns 目标对象 id。
     */
    public resolveMeshId(hitObject: SelectionTargetObject, context: SelectionExtensionContext): string | null {
        if (!this.isGlbHierarchyObject(hitObject)) {
            return null;
        }

        if (context.selectionMode === 'face') {
            return this.findNearestGlbMesh(hitObject)?.uuid ?? null;
        }

        if (hitObject instanceof THREE.Mesh) {
            return hitObject.uuid;
        }

        if (hitObject instanceof THREE.Line) {
            return ImportedModelSelectionObjectResolver.normalizeImportedModelHitObject(hitObject).uuid;
        }

        return this.findNearestGlbOwner(hitObject)?.uuid ?? null;
    }

    /**
     * 根据对象 id 找回场景对象。
     *
     * @param meshId 目标对象 id。
     * @param context 选择上下文。
     * @returns 命中的场景对象。
     */
    public resolveObjectById(meshId: string, context: SelectionExtensionContext): SelectionTargetObject | null {
        let targetObject: SelectionTargetObject | null = null;

        context.scene.traverse((object) => {
            if (!targetObject && object.uuid === meshId && this.isGlbHierarchyObject(object)) {
                targetObject = object;
            }
        });

        return targetObject;
    }

    /**
     * 设置 selected overlay。
     *
     * @param payload 选择事件。
     */
    public onSelected(payload: SelectionExtensionSelectionPayload): void {
        const targets = this.resolveOverlayTargets(payload);
        const existingRenderer = this.peekOverlayRenderer(payload.context.scene);

        if (!targets || targets.length === 0) {
            existingRenderer?.clearSelected();
            return;
        }

        const renderer = existingRenderer ?? this.getOverlayRenderer(payload.context.scene);
        renderer.setSelected(targets);
    }

    /**
     * 设置命令态 selected overlay。
     *
     * 设计说明：
     * 1. 配合命令需要同时高亮两个已吸收实体，普通 `onSelected` 只能表达最后一次选择；
     * 2. 这里复用同一 selected overlay 通道，避免为了命令态再创建第二套材质与几何资源。
     *
     * @param payload 命令态多选高亮载荷。
     */
    public onCommandSelected(payload: SelectionExtensionCommandSelectionPayload): void {
        const targets = payload.items.flatMap((item) => this.resolveOverlayTargets(item));
        const existingRenderer = this.peekOverlayRenderer(payload.context.scene);

        if (targets.length === 0) {
            existingRenderer?.clearCommandSelected();
            return;
        }

        const renderer = existingRenderer ?? this.getOverlayRenderer(payload.context.scene);
        renderer.setCommandSelected(targets);
    }

    /**
     * 清空 selected overlay。
     *
     * @param context 选择上下文。
     */
    public onSelectionCleared(context: SelectionExtensionContext): void {
        this.peekOverlayRenderer(context.scene)?.clearSelected();
    }

    /**
     * 设置 hover overlay。
     *
     * @param payload 悬停事件。
     */
    public onHovered(payload: SelectionExtensionSelectionPayload): void {
        const targets = this.resolveOverlayTargets(payload);
        const existingRenderer = this.peekOverlayRenderer(payload.context.scene);

        if (!targets || targets.length === 0) {
            existingRenderer?.clearHover();
            return;
        }

        const renderer = existingRenderer ?? this.getOverlayRenderer(payload.context.scene);
        renderer.setHover(targets);
    }

    /**
     * 清空 hover overlay。
     *
     * @param context 选择上下文。
     */
    public onHoverCleared(context: SelectionExtensionContext): void {
        this.peekOverlayRenderer(context.scene)?.clearHover();
    }

    /**
     * 面选择兼容桥接。
     * 说明：
     * 1. Task 3 只需要保留 face 模式可用；
     * 2. 这里直接把命中 mesh 作为单目标覆盖，避免再走旧 mesh 子高亮路径。
     *
     * @param payload 面选择事件。
     */
    public onFaceSelected(payload: SelectionExtensionFaceSelectionPayload): void {
        if (!payload.context.highlightEnabled || !this.isGlbHierarchyObject(payload.targetObject)) {
            this.peekOverlayRenderer(payload.context.scene)?.clearSelected();
            return;
        }

        const renderer = this.peekOverlayRenderer(payload.context.scene) ?? this.getOverlayRenderer(payload.context.scene);
        renderer.setSelected([
            {
                object: payload.targetObject,
            },
        ]);
    }

    /**
     * part / assembly overlay 已改为事件驱动，无需额外 pass。
     *
     * @param _context 渲染上下文。
     */
    public renderHighlightPass(_context: SelectionExtensionRenderContext): void {
        // 兼容旧协议，无额外渲染工作。
    }

    /**
     * 释放 bridge 自身持有的 renderer。
     */
    public dispose(): void {
        this.overlayRendererByScene.forEach((renderer) => renderer.dispose());
        this.overlayRendererByScene.clear();
    }

    /**
     * 获取指定场景对应的 overlay renderer。
     *
     * @param scene 当前场景。
     * @returns 对应 renderer。
     */
    private getOverlayRenderer(scene: THREE.Scene): ImportedModelOverlayRenderer {
        const cachedRenderer = this.overlayRendererByScene.get(scene);
        if (cachedRenderer) {
            return cachedRenderer;
        }

        const renderer = new ImportedModelOverlayRenderer(scene);
        this.overlayRendererByScene.set(scene, renderer);
        return renderer;
    }

    /**
     * 只读取已存在的 overlay renderer，不触发创建。
     *
     * @param scene 当前场景。
     * @returns 已存在的 renderer；不存在时返回 `null`。
     */
    private peekOverlayRenderer(scene: THREE.Scene): ImportedModelOverlayRenderer | null {
        return this.overlayRendererByScene.get(scene) ?? null;
    }

    /**
     * 根据交互元信息解析 overlay 目标。
     *
     * @param payload 选择/悬停事件。
     * @returns overlay 目标集合。
     */
    private resolveOverlayTargets(payload: SelectionExtensionSelectionPayload) {
        const targetObject = this.normalizeOverlayTargetObject(payload.targetObject);

        if (!payload.context.highlightEnabled || !this.isGlbHierarchyObject(targetObject)) {
            return [];
        }

        const explicitTargets = this.resolveExplicitInteractionTargets(targetObject, payload);
        if (explicitTargets.length > 0) {
            return explicitTargets;
        }

        if (payload.interactionMeta?.glbNodeRole === 'body') {
            return this.bodyLeafTargetResolver.resolve(targetObject);
        }

        return this.assemblyPartTargetResolver.resolve(targetObject);
    }

    /**
     * 判断对象是否属于 GLB imported-model 语义层。
     *
     * @param object 输入对象。
     * @returns 是否属于 GLB 层级。
     */
    private isGlbHierarchyObject(object: THREE.Object3D | null): boolean {
        return ImportedModelSelectionObjectResolver.isImportedModelObject(object);
    }

    /**
     * 向上查找最近的 GLB mesh。
     *
     * @param object 起点对象。
     * @returns 命中的 mesh。
     */
    private findNearestGlbMesh(object: THREE.Object3D): THREE.Mesh | null {
        let current: THREE.Object3D | null = object;
        while (current) {
            if (current instanceof THREE.Mesh && this.isGlbHierarchyObject(current)) {
                return current;
            }
            current = current.parent;
        }
        return null;
    }

    /**
     * 向上查找最近的 GLB 所有者对象。
     *
     * @param object 起点对象。
     * @returns 命中的对象。
     */
    private findNearestGlbOwner(object: THREE.Object3D): THREE.Object3D | null {
        let current: THREE.Object3D | null = object;
        while (current) {
            if (this.isGlbHierarchyObject(current)) {
                return current;
            }
            current = current.parent;
        }
        return null;
    }

    /**
     * 优先消费事件层显式声明的精确高亮目标。
     *
     * @param payload 当前事件。
     * @returns 精确 overlay 目标列表。
     */
    private resolveExplicitInteractionTargets(
        targetObject: THREE.Object3D,
        payload: SelectionExtensionSelectionPayload,
    ) {
        if (
            !Array.isArray(payload.interactionMeta?.entityHighlightTargets)
            || payload.interactionMeta.entityHighlightTargets.length === 0
        ) {
            return [];
        }

        return payload.interactionMeta.entityHighlightTargets.flatMap((highlightTarget) => {
            const matchedObject = targetObject.uuid === highlightTarget.objectId
                ? targetObject
                : targetObject.getObjectByProperty('uuid', highlightTarget.objectId) ?? null;
            if (!matchedObject) {
                return [];
            }

            return expandImportedModelRenderableTargets(
                matchedObject,
                this.normalizePrimitiveIndices(highlightTarget.primitiveIndices),
            );
        });
    }

    /**
     * 归一化 primitiveIndices。
     *
     * @param primitiveIndices 原始 primitive 下标。
     * @returns 过滤后的 primitive 集合。
     */
    private normalizePrimitiveIndices(primitiveIndices?: number[]): number[] | undefined {
        if (!Array.isArray(primitiveIndices) || primitiveIndices.length === 0) {
            return undefined;
        }

        const normalized = primitiveIndices.filter(
            (primitiveIndex) => Number.isInteger(primitiveIndex) && primitiveIndex >= 0,
        );

        return normalized.length > 0 ? normalized : undefined;
    }

    /**
     * 把 line 命中归一到最近的 GLB owner，避免出现“命中了 line 但 resolver 找不到 mesh”。
     *
     * @param targetObject 当前命中的对象。
     * @returns 归一化后的高亮起点对象。
     */
    private normalizeOverlayTargetObject(targetObject: THREE.Object3D): THREE.Object3D {
        return ImportedModelSelectionObjectResolver.normalizeImportedModelHitObject(targetObject);
    }
}
